// Push-notification destinations: what is configured, and how to add one.
//
// The list is the page. Adding used to be an always-open form beneath it with
// every field rendered and Slack preselected, which is the shape
// `OverlayDialog` was written to replace -- so adding now opens a dialog and
// asks nothing until a destination is chosen.

import { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../ConfirmDialog.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { listSignals } from "../../lib/signalsApi.js";
import { restGet, restPost } from "../../lib/restClient.js";
import AddDestinationDialog from "./AddDestinationDialog.jsx";
import DestinationList from "./DestinationList.jsx";
import { secondaryBtnCls } from "./formControls.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

export default function PushNotificationsPanel({ canManage = true }) {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    // Two separate failures, because they mean opposite things about the list. An
    // action that failed leaves the list valid and on screen; a load that failed
    // means we do not know what the tenant has, and rendering the empty state
    // then asserts it has nothing.
    const [error, setError] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [signals, setSignals] = useState([]);
    const [adding, setAdding] = useState(false);
    const [pendingRemove, setPendingRemove] = useState(null);

    const refetch = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await restGet("/v1/push-notification-integrations");
            setIntegrations(resp?.integrations ?? []);
            setLoadError(null);
        } catch (err) {
            setLoadError(err.message || String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refetch();
    }, [refetch]);

    // Only detections publish notifications, so only detections can be picked.
    // A failure here leaves the picker empty rather than taking the page down —
    // the wildcard still works without tenant.signals.read.
    useEffect(() => {
        let cancelled = false;
        listSignals()
            .then((all) => {
                if (!cancelled) setSignals(all.filter((s) => s.kind === "detection"));
            })
            .catch(() => {
                if (!cancelled) setSignals([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const signalNamesById = useMemo(
        () => Object.fromEntries(signals.map((s) => [s.id, s.displayName || s.name])),
        [signals],
    );

    const onAdd = useCallback(
        async (payload) => {
            await restPost("/v1/push-notification-integrations", payload);
            // A failure from an earlier action is stale once a create succeeds.
            setError(null);
            await refetch();
            // The dialog closes on success, so the confirmation belongs here.
            // It stays until the next action rather than timing out; nothing
            // else on this panel disappears on its own.
            setNotice("Destination added");
        },
        [refetch],
    );

    const onToggle = useCallback(
        async (integration) => {
            setError(null);
            setNotice(null);
            try {
                await restPost("/v1/push-notification-integrations:set-enabled", {
                    integration_id: integration.id,
                    enabled: !integration.enabled,
                });
                await refetch();
            } catch (err) {
                setError(err.message || String(err));
            }
        },
        [refetch],
    );

    const onRemove = useCallback(
        async (integration) => {
            setError(null);
            setNotice(null);
            try {
                await restPost("/v1/push-notification-integrations:delete", {
                    integration_id: integration.id,
                });
            } catch (err) {
                // 404 means it is already gone — someone removed it in another
                // session, or from the database directly. The list is simply
                // stale, and the user asked for exactly the state they now
                // have, so refresh rather than report a failure.
                if (err?.status !== 404) {
                    setError(err.message || String(err));
                    // Refetching here would clear the message it just set, so
                    // the failure would flash and vanish. The list is unchanged
                    // anyway -- the delete did not happen.
                    return;
                }
            }
            await refetch();
        },
        [refetch],
    );

    return (
        <section
            id="notifications-destinations"
            className="grid gap-[10px]"
            aria-busy={loading ? "true" : undefined}
        >
            <div className="flex items-center justify-between gap-[10px] border-b border-[color:var(--line-soft)] pb-[8px]">
                <span className={NOTE_CLASS}>
                    {/* No count when the load failed: a number would be a claim
                        about what the tenant has, which is the thing we do not
                        know. */}
                    {loading
                        ? "Loading…"
                        : loadError
                          ? "Destinations"
                          : `${integrations.length} destination${integrations.length === 1 ? "" : "s"}`}
                </span>
                <span className="flex items-center gap-[8px]">
                    {notice ? (
                        <span className="text-[11px] text-[color:var(--green)]">{notice}</span>
                    ) : null}
                    {error ? <span className="text-[11px] text-red-400">{error}</span> : null}
                    <button
                        type="button"
                        onClick={() => {
                            setNotice(null);
                            setAdding(true);
                        }}
                        aria-pressed={adding}
                        className={secondaryBtnCls}
                    >
                        Add destination
                    </button>
                </span>
            </div>

            {loading ? null : loadError ? (
                <p className="text-[11px] leading-[1.35] text-red-400" role="alert">
                    {loadError} Reload to try again.
                </p>
            ) : (
                <DestinationList
                    integrations={integrations}
                    signalNamesById={signalNamesById}
                    canManage={canManage}
                    onToggle={onToggle}
                    onRemove={setPendingRemove}
                />
            )}

            <AddDestinationDialog
                open={adding}
                onClose={() => setAdding(false)}
                onSubmit={onAdd}
                signals={signals}
            />

            <ConfirmDialog
                open={pendingRemove != null}
                analyticsId={DIALOG.NOTIFICATION_DESTINATION_REMOVE}
                eyebrow="Notifications"
                title={`Remove ${pendingRemove?.displayName ?? pendingRemove?.display_name ?? ""}?`}
                body="This destination stops receiving notifications straight away. Its credentials are deleted, so you would need to enter them again to set this up later."
                confirmLabel="Remove"
                destructive
                onConfirm={() => {
                    const target = pendingRemove;
                    setPendingRemove(null);
                    if (target) onRemove(target);
                }}
                onCancel={() => setPendingRemove(null)}
            />
        </section>
    );
}
